<script setup>
/**
 * Pipeline Onboarding Tour Component
 * UX/UI Sprint - Task B3: Onboarding Tour
 *
 * Visual component for the guided tour overlay and tooltips.
 * Uses Teleport to render at document root for proper z-index handling.
 */

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false,
  },
  currentStep: {
    type: Object,
    default: null,
  },
  currentStepIndex: {
    type: Number,
    default: 0,
  },
  totalSteps: {
    type: Number,
    default: 0,
  },
  progress: {
    type: Number,
    default: 0,
  },
  isFirstStep: {
    type: Boolean,
    default: true,
  },
  isLastStep: {
    type: Boolean,
    default: false,
  },
  targetRect: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['next', 'prev', 'skip', 'complete']);

const { t } = useI18n();

// True when target element is absent on a step that expected one
const isTargetMissing = computed(
  () =>
    !!props.currentStep?.target &&
    props.currentStep.position !== 'center' &&
    !props.targetRect
);

// Use reduced opacity on overlay when no highlight is shown (less alarming UX)
const overlayStyle = computed(() => {
  if (!props.currentStep?.highlight || isTargetMissing.value) {
    return { background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' };
  }
  return {};
});

// Compute tooltip position based on target and position preference
const tooltipStyle = computed(() => {
  const step = props.currentStep;
  if (!step) return {};

  // Center position (no target, or target not found)
  if (step.position === 'center' || !props.targetRect) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const rect = props.targetRect;
  const padding = 16;
  const tooltipWidth = 320;
  const tooltipHeight = 200; // Approximate

  let top;
  let left;

  switch (step.position) {
    case 'top':
      top = rect.top - tooltipHeight - padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = rect.bottom + padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - padding;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + padding;
      break;
    default:
      top = rect.bottom + padding;
      left = rect.left;
  }

  // Keep tooltip within viewport
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (left < padding) left = padding;
  if (left + tooltipWidth > viewportWidth - padding) {
    left = viewportWidth - tooltipWidth - padding;
  }
  if (top < padding) top = padding;
  if (top + tooltipHeight > viewportHeight - padding) {
    top = viewportHeight - tooltipHeight - padding;
  }

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
  };
});

// Compute highlight overlay style
const highlightStyle = computed(() => {
  if (!props.targetRect || !props.currentStep?.highlight) {
    return { display: 'none' };
  }

  const rect = props.targetRect;
  const padding = 8;

  return {
    position: 'fixed',
    top: `${rect.top - padding}px`,
    left: `${rect.left - padding}px`,
    width: `${rect.width + padding * 2}px`,
    height: `${rect.height + padding * 2}px`,
  };
});

const handleNext = () => {
  if (props.isLastStep) {
    emit('complete');
  } else {
    emit('next');
  }
};

const handlePrev = () => {
  emit('prev');
};

const handleSkip = () => {
  emit('skip');
};
</script>

<template>
  <Teleport to="body">
    <Transition name="tour-fade">
      <div v-if="isActive" class="pipeline-tour">
        <!-- Overlay backdrop -->
        <div
          class="pipeline-tour__overlay"
          :style="overlayStyle"
          @click="handleSkip"
        />

        <!-- Highlight box around target element -->
        <div
          v-if="targetRect && currentStep?.highlight"
          class="pipeline-tour__highlight"
          :style="highlightStyle"
        />

        <!-- Tooltip card -->
        <div class="pipeline-tour__tooltip" :style="tooltipStyle">
          <!-- Progress bar -->
          <div class="pipeline-tour__progress">
            <div
              class="pipeline-tour__progress-bar"
              :style="{ width: `${progress}%` }"
            />
          </div>

          <!-- Content -->
          <div class="pipeline-tour__content">
            <!-- Step indicator -->
            <div class="pipeline-tour__step-indicator">
              {{ currentStepIndex + 1 }} / {{ totalSteps }}
            </div>

            <!-- Title -->
            <h3 class="pipeline-tour__title">
              {{ currentStep?.title }}
            </h3>

            <!-- Description -->
            <p class="pipeline-tour__description">
              {{ currentStep?.content }}
            </p>
          </div>

          <!-- Actions -->
          <div class="pipeline-tour__actions">
            <button
              v-if="currentStep?.showSkip !== false && !isLastStep"
              class="pipeline-tour__skip"
              @click="handleSkip"
            >
              {{ t('PIPELINE.TOUR.SKIP') }}
            </button>

            <div class="pipeline-tour__nav">
              <Button
                v-if="!isFirstStep"
                variant="faded"
                color="slate"
                size="sm"
                @click="handlePrev"
              >
                {{ t('PIPELINE.TOUR.PREVIOUS') }}
              </Button>

              <Button
                variant="solid"
                color="blue"
                size="sm"
                @click="handleNext"
              >
                {{
                  isLastStep
                    ? t('PIPELINE.TOUR.FINISH')
                    : t('PIPELINE.TOUR.NEXT')
                }}
              </Button>
            </div>
          </div>

          <!-- Keyboard hint -->
          <div class="pipeline-tour__keyboard-hint">
            <!-- Arrow / Esc glyphs are universal keyboard symbols -->
            <kbd>{{ '←' }}</kbd> <kbd>{{ '→' }}</kbd>
            {{ t('PIPELINE.TOUR.KEYBOARD_NAV') }}
            <span class="mx-2">{{ '|' }}</span>
            <kbd>{{ 'Esc' }}</kbd> {{ t('PIPELINE.TOUR.TO_SKIP') }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.pipeline-tour {
  position: fixed;
  inset: 0;
  z-index: 9300; /* tour-backdrop (tailwind) */
  pointer-events: none;
}

.pipeline-tour__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  pointer-events: auto;
  cursor: pointer;
}

.dark .pipeline-tour__overlay {
  background: rgba(0, 0, 0, 0.75);
}

.pipeline-tour__highlight {
  border-radius: 8px;
  box-shadow:
    0 0 0 4px rgba(59, 130, 246, 0.5),
    0 0 0 9999px rgba(0, 0, 0, 0.6);
  z-index: 9310; /* tour-highlight (tailwind) */
  pointer-events: none;
  transition: all var(--animation-duration-moderate, 200ms)
    var(--animation-ease-default, ease);
}

.dark .pipeline-tour__highlight {
  box-shadow:
    0 0 0 4px rgba(96, 165, 250, 0.5),
    0 0 0 9999px rgba(0, 0, 0, 0.75);
}

.pipeline-tour__tooltip {
  width: min(320px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  background: white;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  z-index: 9320; /* tour-popover (tailwind) */
  pointer-events: auto;
  overflow: hidden;
  animation: tooltipEnter var(--animation-duration-moderate, 200ms)
    var(--animation-ease-enter, ease-out);
}

.dark .pipeline-tour__tooltip {
  background: #1e293b;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.3),
    0 8px 10px -6px rgb(0 0 0 / 0.2);
}

@keyframes tooltipEnter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pipeline-tour__progress {
  height: 3px;
  background: #e2e8f0;
}

.dark .pipeline-tour__progress {
  background: #334155;
}

.pipeline-tour__progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  transition: width var(--animation-duration-slow, 250ms)
    var(--animation-ease-default, ease);
}

.pipeline-tour__content {
  padding: 20px;
}

.pipeline-tour__step-indicator {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 8px;
}

.dark .pipeline-tour__step-indicator {
  color: #94a3b8;
}

.pipeline-tour__title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.dark .pipeline-tour__title {
  color: #f1f5f9;
}

.pipeline-tour__description {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.dark .pipeline-tour__description {
  color: #94a3b8;
}

.pipeline-tour__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.dark .pipeline-tour__actions {
  border-top-color: #334155;
  background: #0f172a;
}

.pipeline-tour__skip {
  font-size: 13px;
  color: #64748b;
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: all var(--animation-duration-fast, 100ms)
    var(--animation-ease-default, ease);
}

.pipeline-tour__skip:hover {
  color: #475569;
  background: #e2e8f0;
}

.dark .pipeline-tour__skip {
  color: #94a3b8;
}

.dark .pipeline-tour__skip:hover {
  color: #cbd5e1;
  background: #334155;
}

.pipeline-tour__nav {
  display: flex;
  gap: 8px;
}

.pipeline-tour__keyboard-hint {
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
  padding: 8px 20px 12px;
  background: #f8fafc;
}

.dark .pipeline-tour__keyboard-hint {
  color: #64748b;
  background: #0f172a;
}

.pipeline-tour__keyboard-hint kbd {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-family: inherit;
  background: #e2e8f0;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  margin: 0 2px;
}

.dark .pipeline-tour__keyboard-hint kbd {
  background: #334155;
  border-color: #475569;
}

/* Transition classes */
.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity var(--animation-duration-moderate, 200ms)
    var(--animation-ease-default, ease);
}

.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .pipeline-tour__content {
    padding: 16px;
  }

  .pipeline-tour__actions {
    padding: 10px 16px;
  }

  .pipeline-tour__keyboard-hint {
    padding: 6px 16px 10px;
  }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .pipeline-tour__tooltip {
    animation: none;
  }

  .pipeline-tour__highlight,
  .pipeline-tour__progress-bar {
    transition: none;
  }

  .tour-fade-enter-active,
  .tour-fade-leave-active {
    transition: none;
  }
}
</style>
